import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { NV_PATHS, nvLoginPath } from "@/components/navigator/nv-paths";
import { useAuth } from "@/contexts/auth-context";
import { accessReasonOf, type AccessDenialReason } from "@/lib/access";

// 이용 권한 안내 팝업(NvAccessDialog) 공통 동작: 열림 상태 · 로그인 / 회원가입 이동.
// 팝업 문구는 화면마다 달라서 loginMessage / forbiddenMessage는 각 화면이 NvAccessDialog에 넘겨요.
//
//   const gate = useAccessGate();
//   if (gate.denied) return gate.show();                    // 지금 등급이 못 쓰는 기능을 눌렀을 때
//   <NvAccessDialog {...gate.dialogProps} loginMessage="…" />
//
// 서버가 거절 코드로 답한 화면(상세 · 정의서)은 gate.show(reason)을 쓰거나, 사유를 직접 정해
// <NvAccessDialog {...gate.dialogProps} open={reason !== null} reason={reason} onOpenChange={…} /> 로 넘겨요.
export function useAccessGate() {
  const { accessLevel } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [reason, setReason] = useState<AccessDenialReason | null>(null);

  // 지금 등급이 이용할 수 없는 이유 (null = 이용 가능)
  const denied = accessReasonOf(accessLevel);

  // 팝업 열기: 사유를 안 주면 지금 등급 기준 (등급을 모르면 비회원 안내)
  const show = useCallback((next?: AccessDenialReason) => setReason(next ?? denied ?? "LOGIN_REQUIRED"), [denied]);
  const hide = useCallback(() => setReason(null), []);

  // 로그인 뒤 지금 보던 화면(검색어 · 필터 쿼리 포함)으로 돌아와요
  const goLogin = useCallback(() => navigate(nvLoginPath(location.pathname + location.search)), [navigate, location.pathname, location.search]);
  const goSignup = useCallback(() => navigate(NV_PATHS.signup), [navigate]);

  const dialogProps = {
    open: reason !== null,
    reason,
    onOpenChange: (open: boolean) => {
      if (!open) hide();
    },
    onLogin: goLogin,
    onSignup: goSignup,
  };

  return { denied, reason, show, hide, goLogin, goSignup, dialogProps };
}
